import React, { useCallback, useEffect, useMemo, useState } from 'react'
import * as Modal from '_components/modal/generic-modal'
import { Loader } from '@refera/ui-web'

import useStyles from './styles'
import { FormProvider, useForm } from 'react-hook-form'
import { Checkbox, DateTimePicker } from '_/components/inputs'
import { useDispatch, useSelector } from 'react-redux'
import { useToast } from '_/hooks/use-toast'
import { Grid, Typography } from '@mui/material'
import { getTradesmanSequenceResults } from '_/modules/tradesman-sequence/selectors'
import moment from 'moment'
import { currentServiceOrderSelector } from '_/modules/service-orders/selectors'
import { acceptByTradesman, acceptIBByTradesman } from '_/modules/tradesman-sequence/actions'
import { getServiceOrder } from '_/modules/service-orders/actions'
import { getStepStatusLog } from '_/modules/budget/actions'
import { getMessagingByServiceOrderId } from '_/modules/mensageria/actions'
import { ROLE_GROUP_CHOICES } from '_/utils/constants/service-order'
import { openWhatsAppWithMessage } from '_/utils/open-whatsApp'
import Select from './Select'

/**
 * @typedef {Object} EndActionFnParams
 * @property {'success' | 'error' | 'unfinished'} [EndActionFnParams.reason] - The reason for ending the action.
 * @property {string} [EndActionFnParams.message] - The optional message for the action.
 */

/**
 * @typedef {Object} AcceptByTradesmanModalProps
 * @property {boolean} [AcceptByTradesmanModalProps.open] - Whether the modal is open or not.
 * @property {(params?: EndActionFnParams) => void} [AcceptByTradesmanModalProps.handleClose] - The function to close the modal.
 * @property {boolean} [AcceptByTradesmanModalProps.isInnerBudget] - Whether the budget is inner or not.
 * @property {boolean} [AcceptByTradesmanModalProps.supressSubmitSideEffects] - Whether to supress submit side effects. Defaults to false.
 */

/**
 * @param {AcceptByTradesmanModalProps} props
 */
export default function AcceptByTradesmanModal({
  open,
  onClose,
  isInnerBudget = false,
  supressSubmitSideEffects = false,
}) {
  const styles = useStyles()
  const dispatch = useDispatch()
  const { showToast } = useToast()

  const serviceOrder = useSelector(currentServiceOrderSelector)
  const serviceOrderId = serviceOrder?.id
  const [loading, setLoading] = useState(false)
  const tradesmanSequenceResults = useSelector(getTradesmanSequenceResults)

  const defaultTradesman = useMemo(() => {
    if (!tradesmanSequenceResults?.length) {
      return null
    }

    const lastSequence = tradesmanSequenceResults.slice().sort((a, b) => b?.id - a?.id)[0]
    if (!lastSequence?.poolDeadline) {
      return null
    }

    return lastSequence?.candidates?.[0]?.serviceProvider?.id
  }, [tradesmanSequenceResults])

  const providersList = useMemo(() => {
    const seen = new Set()

    if (!tradesmanSequenceResults?.length) {
      return []
    }

    const lastSequence = tradesmanSequenceResults.slice().sort((a, b) => b?.id - a?.id)[0]

    return (
      lastSequence?.candidates?.reduce((acc, candidate) => {
        const provider = candidate?.serviceProvider
        const unappointedAt = candidate?.unappointedAt

        if (unappointedAt == null && provider && !seen.has(provider.id)) {
          seen.add(provider.id)
          acc.push(provider)
        }
        return acc
      }, []) ?? []
    )
  }, [tradesmanSequenceResults])

  function formatDateTime(dateStr, startTime, endTime) {
    return `${moment(dateStr).format('DD/MM/YYYY')} das ${moment(startTime, 'HH:mm:ss').format(
      'HH:mm'
    )} às ${moment(endTime, 'HH:mm:ss').format('HH:mm')}`
  }

  useEffect(() => {
    const hasProvidersRefera = providersList?.find(item => item?.recordOwner === null)
    if (hasProvidersRefera) {
      showToast({
        type: 'warning',
        message: isInnerBudget
          ? 'Este chamado possui prestadores gerenciados pela Refera atrelados. Prestadores gerenciados pela Refera não permitem a aceitação de orçamentos internos internamente.'
          : 'Este chamado possui prestadores gerenciados pela Refera atrelados. Prestadores gerenciados pela Refera não permitem a aceitação de chamados internamente.',
      })
    }
  }, [isInnerBudget, providersList])

  const methods = useForm({
    mode: 'all',
    defaultValues: {
      tradesman: defaultTradesman,
      notDateNegotiation: false,
      dateAndTimeNegotiation: null,
    },
  })

  const { handleSubmit, errors, watch } = methods

  const disabledDateTimePicker = watch('notDateNegotiation') === true

  const onSubmit = useCallback(
    async data => {
      setLoading(true)

      try {
        if (isInnerBudget)
          await dispatch(
            acceptIBByTradesman(serviceOrderId, {
              tradesmanId: data.tradesman,
            })
          )
        else
          await dispatch(
            acceptByTradesman(serviceOrderId, {
              tradesmanId: data.tradesman,
              dateAndTimeNegotiation: data.notDateNegotiation
                ? null
                : data.dateAndTimeNegotiation.toISOString(),
            })
          )

        await Promise.all([
          dispatch(getServiceOrder(serviceOrderId)).then(async responseSO => {
            await dispatch(
              getMessagingByServiceOrderId({
                serviceOrderId,
                destination: ROLE_GROUP_CHOICES.TRADESMAN_GROUP,
              })
            ).then(response => {
              if (response?.whatsappText)
                openWhatsAppWithMessage({
                  phone: responseSO?.tradesmanPhone,
                  message: response.whatsappText,
                })
            })
          }),
          !supressSubmitSideEffects && dispatch(getStepStatusLog(serviceOrderId)),
        ])
        !supressSubmitSideEffects &&
          showToast({ type: 'success', message: 'Aceite realizado com sucesso' })
        onClose({ reason: 'success' })
      } catch (error) {
        showToast({ type: 'error' })
      } finally {
        setLoading(false)
      }
    },
    [onClose, supressSubmitSideEffects]
  )

  const hasValidDates =
    (serviceOrder?.firstSuggestionDate &&
      serviceOrder?.firstStartTime &&
      serviceOrder?.firstEndTime) ||
    (serviceOrder?.secondSuggestionDate &&
      serviceOrder?.secondStartTime &&
      serviceOrder?.secondEndTime) ||
    (serviceOrder?.thirdSuggestionDate &&
      serviceOrder?.thirdStartTime &&
      serviceOrder?.thirdEndTime)

  return (
    <Modal.Root open={open} onClose={onClose} maxWidth="md">
      <Modal.TitleModal
        title={
          isInnerBudget
            ? 'Aceite de Orçamento Interno pelo prestador'
            : 'Aceite de chamado pelo prestador'
        }
      />

      <Modal.Content className={styles.container}>
        <FormProvider {...methods}>
          <form className={styles.box} id="formEmail">
            <Grid className={styles.column}>
              <Typography className={styles.title}>
                {`Selecione no campo abaixo o prestador que você está aceitando o ${
                  isInnerBudget ? 'orçamento' : 'chamado'
                } por ele.`}
              </Typography>
            </Grid>
            <Grid className={styles.column}>
              <Select
                label="Prestador"
                name="tradesman"
                defaultValue={defaultTradesman}
                required
                error={!!errors?.tradesman}
                options={providersList}
                getLabel={item => item.name}
                getValue={item => item.id}
                getKey={item => item.id}
                menuProps={{
                  getContentAnchorEl: null,
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'center',
                  },
                  transformOrigin: {
                    vertical: 'top',
                    horizontal: 'center',
                  },
                }}
                style={{ gridArea: 'businessFront' }}
                containerClasses={styles.select}
              />
            </Grid>

            {!isInnerBudget && (
              <>
                <Grid className={styles.column}>
                  <Typography className={styles.title}>Sugestões do solicitante</Typography>

                  {hasValidDates ? (
                    <>
                      {serviceOrder?.firstSuggestionDate &&
                        serviceOrder?.firstStartTime &&
                        serviceOrder?.firstEndTime && (
                          <Typography className={styles.text}>
                            {formatDateTime(
                              serviceOrder?.firstSuggestionDate,
                              serviceOrder?.firstStartTime,
                              serviceOrder?.firstEndTime
                            )}
                          </Typography>
                        )}
                      {serviceOrder?.secondSuggestionDate &&
                        serviceOrder?.secondStartTime &&
                        serviceOrder?.secondEndTime && (
                          <Typography className={styles.text}>
                            {formatDateTime(
                              serviceOrder?.secondSuggestionDate,
                              serviceOrder?.secondStartTime,
                              serviceOrder?.secondEndTime
                            )}
                          </Typography>
                        )}
                      {serviceOrder?.thirdSuggestionDate &&
                        serviceOrder?.thirdStartTime &&
                        serviceOrder?.thirdEndTime && (
                          <Typography className={styles.text}>
                            {formatDateTime(
                              serviceOrder?.thirdSuggestionDate,
                              serviceOrder?.thirdStartTime,
                              serviceOrder?.thirdEndTime
                            )}
                          </Typography>
                        )}
                    </>
                  ) : (
                    <Typography className={styles.text}>Sem datas sugeridas</Typography>
                  )}
                </Grid>

                <Grid className={styles.column}>
                  <Checkbox
                    label="Deixar prestador negociar a data com cliente"
                    name="notDateNegotiation"
                    defaultValue={false}
                    containerClasses={styles.checkboxContainer}
                  />
                </Grid>

                <Grid className={styles.column}>
                  <DateTimePicker
                    name="dateAndTimeNegotiation"
                    label="Data e hora de visita para orçar"
                    referaDatePickerProps={{ disablePast: true, disabled: disabledDateTimePicker }}
                    required={!disabledDateTimePicker}
                  />
                </Grid>
              </>
            )}
          </form>
        </FormProvider>
      </Modal.Content>

      <Modal.Actions>
        <Modal.ButtonRed onClick={onClose} disabled={loading}>
          Cancelar
        </Modal.ButtonRed>
        <Modal.ButtonFullBlue form="formEmail" onClick={handleSubmit(onSubmit)} disabled={loading}>
          Salvar
        </Modal.ButtonFullBlue>
      </Modal.Actions>
      <Loader hasBackdrop open={loading} />
    </Modal.Root>
  )
}
